// Print preflight's copyfitting suggestions as margin comments (`CopyfitNotes.place`, SuiteIntegrations): each one on the
// passage it's about, by "Print preflight" as an agent, so it follows the text as it's edited; the text never changes.
// A new set resolves the previous one (those notes describe a layout that no longer exists); placing the same build's
// notes twice adds nothing. A note whose passage can't be found goes into the Inbox, as the Swift's review import does.
//
// Preflight quotes a printed line (plain text, curled quotes, soft hyphens taken out) and its paragraph; the documents
// hold the prose dialect. `findQuote` compares the two folded alike, as the Swift's `TextIndex` does: emphasis markers,
// escapes and span syntax skipped, quotes, dashes and ellipses folded, spacing collapsed, case ignored. Positions are
// Unicode scalars (Automerge's).
import { readFragment, stableUUID, StoreEditor, type AgentComment } from '@writing-suite/core';
import type { PreflightFinding } from '@writing-suite/compile';
import { COPYFIT_AUTHOR, copyfitRemarks } from '@writing-suite/compile/typst';

interface Folded {
  text: string;
  /** Scalar index in the original of each folded character. */
  at: number[];
}

const QUOTES: Record<string, string> = { '‘': "'", '’': "'", '‚': "'", '‛': "'", '′': "'", '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"', '«': '"', '»': '"' };
const DASHES = new Set(['–', '—', '‒', '―', '‐', '‑', '−']);
const SPACE = /[\s  -​  　]/u;

/** Folds text for matching. `markdown`: skip the dialect's inline syntax (emphasis, escapes, spans, footnote refs). */
export function fold(text: string, markdown: boolean): Folded {
  const chars = [...text];
  const out: string[] = [];
  const at: number[] = [];
  const push = (c: string, i: number) => {
    if (c === ' ') {
      if (out.length === 0 || out[out.length - 1] === ' ') return;
    }
    out.push(c);
    at.push(i);
  };
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i];
    if (markdown) {
      if (c === '\\' && i + 1 < chars.length) {
        i++;
        push(foldChar(chars[i]), i);
        continue;
      }
      if (c === '*' || c === '_') continue;
      if (c === '[' && chars[i + 1] === '^') {
        // A footnote reference `[^n]`: not printed in the line.
        const close = chars.indexOf(']', i);
        if (close > 0) { i = close; continue; }
      }
      if (c === '[') continue;
      if (c === ']' && chars[i + 1] === '{') {
        const close = chars.indexOf('}', i);
        if (close > 0) { i = close; continue; }
      }
      if (c === ']') continue;
    }
    if (c === '­') continue;
    if (c === '…') {
      for (let k = 0; k < 3; k++) push('.', i);
      continue;
    }
    push(foldChar(c), i);
  }
  while (out.length && out[out.length - 1] === ' ') { out.pop(); at.pop(); }
  return { text: out.join(''), at };
}

function foldChar(c: string): string {
  if (SPACE.test(c)) return ' ';
  if (QUOTES[c]) return QUOTES[c];
  if (DASHES.has(c)) return '-';
  return c.toLowerCase();
}

/** Where `quote` is in `text` (stored Markdown), in scalars: inside `context` (its paragraph) when that's found too. */
export function findQuote(text: string, quote: string, context?: string): [number, number] | null {
  const hay = fold(text, true);
  const needle = fold(quote, false).text.trim();
  if (needle === '') return null;
  let from = 0, to = hay.text.length;
  if (context) {
    const c = fold(context, false).text.trim();
    const where = c ? hay.text.indexOf(c) : -1;
    if (where >= 0) { from = where; to = where + c.length; }
  }
  let hit = hay.text.indexOf(needle, from);
  if (hit < 0 || hit + needle.length > to) hit = hay.text.indexOf(needle);
  if (hit < 0) return null;
  return [hay.at[hit], hay.at[hit + needle.length - 1] + 1];
}

export interface CopyfitPlacement { placed: number; unplaced: number; resolved: number }

/**
 * `CopyfitNotes.place`: resolves the open notes of an earlier build, then adds this build's, each on its passage.
 * `docs` are the live documents (through `LiveProject.editDocuments`, so the changes sync like any edit).
 */
export function placeCopyfitNotes(docs: Map<string, import('@automerge/automerge').Doc<unknown>>, findings: readonly PreflightFinding[],
  o: { device: string; checkpointId: string | null; now?: Date }): CopyfitPlacement {
  const now = o.now ?? new Date();
  const editor = new StoreEditor(docs, { device: o.device, author: COPYFIT_AUTHOR, now: () => now });
  const remarks = copyfitRemarks(findings, now);
  // The findings that carry a remark, in the same order, for their fragments.
  const sources = findings.filter((f) => f.copyfit !== undefined && f.quote !== undefined && f.quote !== '');
  const comments: AgentComment[] = [];
  const unplaced: string[] = [];
  remarks.forEach((remark, i) => {
    const fragmentID = sources[i]?.fragmentID;
    const doc = fragmentID ? docs.get('fragment.' + fragmentID) : undefined;
    const range = doc ? findQuote(readFragment(doc).text, remark.quote, remark.context) : null;
    if (!fragmentID || !range) {
      unplaced.push(`${remark.body}\n\n“${remark.quote}”`);
      return;
    }
    comments.push({ id: stableUUID(`copyfit|${o.checkpointId ?? ''}|${fragmentID}|${remark.id}`), fragmentID, range, body: remark.body });
  });
  const keep = new Set(comments.map((c) => c.id));
  let resolved = 0;
  for (const [name, doc] of [...docs]) {
    if (!name.startsWith('fragment.')) continue;
    const id = name.slice('fragment.'.length);
    for (const c of readFragment(doc).comments) {
      if (c.author !== COPYFIT_AUTHOR || c.authorKind !== 'agent' || c.status !== 'open' || keep.has(c.id)) continue;
      editor.setCommentStatus(id, c.id, 'resolved');
      resolved++;
    }
  }
  const placed = editor.addAgentComments(comments, COPYFIT_AUTHOR);
  if (unplaced.length) {
    editor.addDraft({
      title: 'Copyfitting notes', text: unplaced.join('\n\n'), notes: 'Print preflight’s notes that couldn’t be matched to the text.',
    });
  }
  return { placed, unplaced: unplaced.length, resolved };
}
